/**
 * ホスト（polaris-mono側の電子ブックビューア等）からU6タグを呼び出すための公開API（M-2対応）。
 * NFR Design 1.2準拠: window上には限定的な名前空間のみを公開し、各タグのdisplayConditionMonitor
 * インスタンスはタグID（objectRootId等の呼び出し元識別子）で区別してレジストリに保持する。
 * タグ間で状態そのものを共有するわけではない（各インスタンスは独立、レジストリは参照の受け渡しのみ）。
 */

import type { DisplayConditionMonitor } from "../w1-survey/displayConditionMonitor";
import { logClientWarning } from "./errorHandler";
import {
  trackPageEvent as trackPageEventImpl,
  notifyPageTransitionToMonitor,
} from "../w3-tracking/trackPageEvent";
import type { PageEventPayload, PageEventType } from "../types";

interface SpicaWidgetHostApi {
  /**
   * ホスト側の電子ブックビューア等から呼び出す。分析イベント（WIDGET_VIEW/ERROR）を送信する
   * （2026-08-04改訂: 表示トリガー監視への通知は{@link notifyPageTransition}へ分離済み）。
   *
   * <p>{@code tagId}はホスト側の既存呼び出し契約（後方互換）のため引数として残しているが、
   * 分析イベント送信自体にはtagIdを使用しない（実装側は{@code _tagId}として受け取り破棄する）。
   */
  trackPageEvent(tagId: string, eventType: PageEventType, payload: PageEventPayload): void;
  /**
   * ホスト側の電子ブックビューア等から呼び出す。tagIdで対象タグのdisplayConditionMonitorに
   * ページ推移を伝える（到達率廃止により現在はno-op、Step12責務分離）。
   * 分析イベント送信は行わない（{@link trackPageEvent}とは独立した窓口）。
   */
  notifyPageTransition(tagId: string, pageNumber: number, totalPages: number): void;
}

const monitorRegistry = new Map<string, DisplayConditionMonitor>();

/** entry.tsの初期化処理から、タグごとのmonitorインスタンスを登録する。 */
export function registerTagMonitor(tagId: string, monitor: DisplayConditionMonitor): void {
  monitorRegistry.set(tagId, monitor);
}

function installHostApi(): void {
  const win = window as unknown as { SpicaWidget?: SpicaWidgetHostApi };
  if (win.SpicaWidget) return; // 複数タグが存在してもAPIオブジェクト自体は1つで足りる（各呼び出しはtagId区別）

  win.SpicaWidget = {
    trackPageEvent(_tagId: string, eventType: PageEventType, payload: PageEventPayload): void {
      trackPageEventImpl(eventType, payload);
    },
    notifyPageTransition(tagId: string, pageNumber: number, totalPages: number): void {
      for (const monitor of resolveMonitors(tagId)) {
        notifyPageTransitionToMonitor(pageNumber, totalPages, monitor);
      }
    },
  };
}

/**
 * 通知先のmonitorを解決する（2026-08-07 バグ修正）。
 *
 * <p>従来はtagIdの完全一致のみで解決し、一致しない場合は通知を黙って捨てていた。
 * ホスト側（CMSコンテンツid=483）が渡すtagIdは`###catalogId.custom###`というCMSプレースホルダの
 * 解決結果であり、widget側の`data-cvs`の`objectRootId`とは別経路で組み立てられているため、
 * 文字列表現のズレ・プレースホルダ未解決といった要因で一致しなくなると到達率条件が
 * 一切成立しなくなる（原因が表面化しないまま「表示されない」だけになる）。
 *
 * <p>ページ推移通知は電子ブック閲覧画面固有であり、同一画面に電子ブック用タグが複数存在する
 * 想定はない（1画面1タグ、BR-U6-10）。そのためtagId不一致時は登録済みの全monitorへ
 * フォールバック配信し、警告ログで不一致を可視化する。
 */
function resolveMonitors(tagId: string): DisplayConditionMonitor[] {
  const exactMatch = monitorRegistry.get(tagId);
  if (exactMatch) {
    return [exactMatch];
  }
  const all = Array.from(monitorRegistry.values());
  if (all.length === 0) {
    // タグ初期化前（readWidgetContext到達前）の通知。ホスト側のポーリングで再通知される。
    logClientWarning(`notifyPageTransition: no tag registered yet (tagId=${tagId})`);
    return [];
  }
  logClientWarning(
    `notifyPageTransition: tagId mismatch, falling back to all registered tags`
      + ` (tagId=${tagId}, registered=${Array.from(monitorRegistry.keys()).join("|")})`
  );
  return all;
}

installHostApi();
